import axios from "axios";
import { cacheAdapterEnhancer } from "axios-extensions";
import Icon from "components/Icons/Icon";
import { idToken } from "components/firebase";
import { useEffect, useMemo, useState, useRef } from "react";
import ImgCredits from "./ImgCredits";
// Quiets axios-extensions' cache adapter, which reads process.env.LOGGER_LEVEL and warns on
// every cache hit without it. Guarded because this runs at import time: the module is reachable
// from the SSR bundle (via b2c/explore/exploreCommon → the /discover guide pages), where
// `window` does not exist and an unguarded write takes the whole bundle down before it loads.
if (typeof window !== "undefined") {
	window.process = { ...window.process, env: { ...window.process?.env, LOGGER_LEVEL: "warn" } };
}

const http = axios.create({
	baseURL: "/",
	headers: {},
	adapter: cacheAdapterEnhancer(axios.defaults.adapter),
});

type ImageType = "large" | "regular" | "small" | "thumb" | "xl";
type ImageSource = "pexels" | "flickr" | "unsplash" | "serper";

interface UnsplashImage {
	urls: {
		regular: string;
	};
	user: {
		username: string;
	};
}

interface UnsplashResponse {
	data: {
		results: UnsplashImage[];
	};
}

interface ImageData {
	links?: {
		html?: string;
	};
	url?: string;
	owner?: string;
	source?: ImageSource;
}

// Helper function to transform image URLs based on type
const transformImageUrl = (url: string | undefined, type: ImageType): string | undefined => {
	if (!url) return undefined;

	const widthMap = { large: 800, regular: 600, small: 300, thumb: 200, xl: 1200 };
	const heightMap = { large: 1080, regular: 400, small: 250, thumb: 150, xl: 1600 };
	const flickrSuffixMap = { large: "b", regular: "z", small: "w", thumb: "t", xl: "h" };

	return url
		// Unsplash
		.replace("w=1080", `w=${widthMap[type]}&auto=compress,format`)
		.replace("cs=tinysrgb&fit=max&fm=jpg&", "")
		// Pexels
		.replace("h=650", `h=${heightMap[type]}`)
		// Flickr
		.replace("_b.", `_${flickrSuffixMap[type]}.`);
};

const getExternalImage = async (country: string, city: string): Promise<UnsplashResponse | "error"> => {
	try {
		// Keep country context so same-named places ("Victoria", parks) resolve to the right photo.
		const query = [city, country].filter(Boolean).join(" ");
		const res = await http.get<{ results: UnsplashImage[] }>(
			`/api/utils/unsplash/${encodeURIComponent(query)}`,
			{ headers: { Authorization: idToken } }
		);
		return { data: res.data };
	} catch {
		return "error";
	}
};

interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	src?: string;
	fallback?: string;
	alt?: string;
	country?: string;
	city?: string;
	caption?: boolean;
	type?: ImageType;
	loadtype?: "lazy" | "eager" | "normal";
	hideCredit?: boolean;
}

const Img = ({
	src = "",
	fallback = "",
	alt = "",
	country = "",
	city = "",
	caption = true,
	type = "regular",
	loadtype = "lazy",
	hideCredit = false,
	...props
}: ImgProps) => {
	const [loading, setLoading] = useState(!src);
	const [usableSrc, setUsableSrc] = useState(src || fallback);
	const [author, setAuthor] = useState("");
	const [source, setSource] = useState<ImageSource | "">("");
	const abortControllerRef = useRef<AbortController | null>(null);

	// Build search query
	const searchQuery = useMemo(() => {
		if (!country && (!city || city === "none")) return "";
		if (country && city && city !== "none") return `country=${country}&city=${city}`;
		if (country) return `country=${country}`;
		return `city=${city}`;
	}, [country, city]);

	useEffect(() => {
		// If src is provided, no need to fetch images
		if (src) {
			setLoading(false);
			setUsableSrc(src);
			return undefined;
		}

		// Abort previous request if it exists
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		const fetchImages = async () => {
			try {
				const res = await http.get<ImageData[]>(`/api/utils/images?${searchQuery}`, {
					headers: { Authorization: idToken },
					signal,
				});

				if (signal.aborted) return;

				if (res?.data?.length > 0) {
					// Use the top-ranked image (curated superLikes first, else Serper's filtered ranking).
					// Deterministic so a destination keeps the same photo across renders/reloads.
					const selectedImage = res.data[0];
					const imageUrl = selectedImage?.links?.html || selectedImage?.url;

					setUsableSrc(transformImageUrl(imageUrl, type) || fallback);
					setAuthor(selectedImage?.owner || "");
					setSource(selectedImage?.source || "");
				} else {
					// Fetch from external Unsplash API
					const externalResult = await getExternalImage(country, city);

					if (signal.aborted) return;

					if (externalResult !== "error" && externalResult.data.results?.length > 0) {
						const firstImage = externalResult.data.results[0];
						setUsableSrc(transformImageUrl(firstImage.urls.regular, type) || fallback);
						setAuthor(firstImage.user?.username || "");
						setSource("unsplash");
					} else {
						setUsableSrc(fallback);
					}
				}
			} catch (err) {
				if (!signal.aborted && !axios.isCancel(err)) {
					console.error("Error fetching images:", err);
					setUsableSrc(fallback);
				}
			} finally {
				if (!signal.aborted) {
					setLoading(false);
				}
			}
		};

		fetchImages();

		// Cleanup function
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [src, searchQuery, type, fallback, country, city]);

	const url = useMemo(
		() =>
			source === "pexels"
				? `https://www.pexels.com`
				: source === "flickr"
				? `https://www.flickr.com/photos/${author}`
				: source === "serper"
				? undefined // scraped Google-image result: the owner is the publisher, with no profile page to link
				: `https://unsplash.com/@${author}?utm_source=Stippl&utm_medium=referral`,
		[source, author]
	);

	if (loading) return null;

	return (
		<figure className="img-wrapper">
			{usableSrc ? (
				<img
					src={usableSrc}
					alt={alt}
					{...props}
					{...(loadtype !== "normal" && { loading: loadtype as "lazy" | "eager" })}
					onError={(e) => {
						// A caller-supplied `srcSet` outranks `src` in the browser, so a failed candidate would
						// keep being re-selected after the fallback `src` below is set. Drop it first.
						e.currentTarget.srcset = "";
						e.currentTarget.src =
							"https://images.unsplash.com/photo-1500835556837-99ac94a94552?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=800&q=80";
					}}
				/>
			) : (
				<div className="image-placeholder">
					<Icon name="stippl" />
				</div>
			)}

			{author && caption && !hideCredit && (
				<ImgCredits
					source={author}
					sourceUrl={url}
					provider={source || "unsplash"}
				/>
			)}
		</figure>
	);
};

export default Img;
